// src/Pages/Menu.js
import React, { useState, useEffect } from 'react';
import { Box, Typography, Container, CircularProgress, Tabs, Tab, Fade, Modal, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { fetchMenuFromSheet, fetchImagesFromDrive, getDriveImageUrl } from '../utils/googleApi';

const MenuPage = () => {
  const [menuItems, setMenuItems] = useState([]);
  const [driveImages, setDriveImages] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(0);
  const [lightbox, setLightbox] = useState(null); // { src, alt } or null

  useEffect(() => {
    const sheetId = process.env.REACT_APP_GOOGLE_SHEET_ID;
    const folderId = process.env.REACT_APP_GOOGLE_DRIVE_MENU_FOLDER_ID;

    const loadData = async () => {
      try {
        // Fetch Drive images for matching to menu items
        if (folderId) {
          const images = await fetchImagesFromDrive(folderId);
          setDriveImages(images);
        }

        // Fetch menu from Google Sheets
        const sheetMenu = await fetchMenuFromSheet(sheetId);
        if (sheetMenu) {
          setMenuItems(sheetMenu);
        }
      } catch (error) {
        console.error('Error loading menu data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  if (loading) {
    return (
      <Container sx={{ py: 8 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <CircularProgress sx={{ color: 'black' }} />
        </Box>
      </Container>
    );
  }

  // No menu data configured
  if (menuItems.length === 0) {
    return (
      <Container maxWidth="sm" sx={{ py: 8 }}>
        <Typography
          variant="h4"
          sx={{
            textAlign: 'center',
            color: 'black',
            fontFamily: '"Audiowide", sans-serif',
            mb: 2,
          }}
        >
          Menu
        </Typography>
        <Typography
          sx={{
            textAlign: 'center',
            color: 'rgba(0,0,0,0.5)',
            fontFamily: 'Roboto, sans-serif',
            fontSize: '18px',
          }}
        >
          Menu coming soon — check back shortly!
        </Typography>
      </Container>
    );
  }

  const activeSection = menuItems[activeTab];

  return (
    <Container maxWidth="lg" sx={{ py: 4, px: { xs: 2, md: 4 } }}>

      {/* ===== LIGHTBOX MODAL ===== */}
      <Modal
        open={!!lightbox}
        onClose={() => setLightbox(null)}
        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      >
        <Box
          sx={{
            position: 'relative',
            outline: 'none',
            maxWidth: '92vw',
            maxHeight: '88vh',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          {/* Exit button */}
          <Box
            sx={{
              width: '100%',
              display: 'flex',
              justifyContent: 'flex-end',
              mb: 1,
            }}
          >
            <IconButton
              onClick={() => setLightbox(null)}
              sx={{
                backgroundColor: 'white',
                color: 'black',
                fontFamily: 'Roboto, sans-serif',
                fontWeight: 'bold',
                fontSize: '14px',
                borderRadius: '4px',
                px: 2,
                py: 0.5,
                gap: 0.5,
                '&:hover': { backgroundColor: '#f0f0f0' },
              }}
            >
              <CloseIcon sx={{ fontSize: 18 }} />
              Exit
            </IconButton>
          </Box>

          {/* Full image */}
          {lightbox && (
            <Box
              component="img"
              src={lightbox.src}
              alt={lightbox.alt}
              sx={{
                maxWidth: '92vw',
                maxHeight: '78vh',
                objectFit: 'contain',
                borderRadius: 2,
                boxShadow: '0 8px 32px rgba(0,0,0,0.6)',
                display: 'block',
              }}
            />
          )}

          {/* Item name caption */}
          {lightbox && (
            <Typography
              sx={{
                mt: 1.5,
                color: 'white',
                fontFamily: 'Roboto, sans-serif',
                fontWeight: 'bold',
                fontSize: { xs: '16px', md: '18px' },
                textAlign: 'center',
                textShadow: '0 1px 4px rgba(0,0,0,0.8)',
              }}
            >
              {lightbox.alt}
            </Typography>
          )}
        </Box>
      </Modal>
      {/* Title */}
      <Typography
        variant="h4"
        component="h2"
        gutterBottom
        sx={{
          textAlign: 'center',
          color: 'black',
          fontWeight: 'normal',
          fontFamily: '"Audiowide", sans-serif',
          mb: 1,
        }}
      >
        Menu
      </Typography>

      {/* Category Tabs */}
      <Box sx={{ display: 'flex', justifyContent: 'center', mb: 4 }}>
        <Tabs
          value={activeTab}
          onChange={(_, newVal) => setActiveTab(newVal)}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{
            '& .MuiTab-root': {
              fontFamily: 'Roboto, sans-serif',
              fontWeight: 'bold',
              color: 'rgba(0,0,0,0.5)',
              fontSize: { xs: '14px', md: '16px' },
              textTransform: 'none',
              px: { xs: 2, md: 3 },
              minHeight: 48,
            },
            '& .Mui-selected': {
              color: 'black !important',
            },
            '& .MuiTabs-indicator': {
              backgroundColor: 'black',
              height: 3,
            },
          }}
        >
          {menuItems.map((section) => (
            <Tab key={section.category} label={section.category} />
          ))}
        </Tabs>
      </Box>

      {/* Menu Items Grid */}
      {activeSection && (
        <Fade in={true} timeout={400} key={activeTab}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, 1fr)',
                md: 'repeat(3, 1fr)',
              },
              gap: 3,
            }}
          >
            {activeSection.items.map((item) => {
              const imageUrl = getDriveImageUrl(item.imageName, driveImages);

              return (
                <Box
                  key={item.name}
                  sx={{
                    backgroundColor: 'white',
                    borderRadius: 2,
                    overflow: 'hidden',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
                    transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
                    },
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  {/* Image from Drive — tap/click to enlarge */}
                  {imageUrl && (
                    <Box
                      component="img"
                      src={imageUrl}
                      alt={item.name}
                      loading="lazy"
                      onClick={() => setLightbox({ src: imageUrl, alt: item.name })}
                      sx={{
                        width: '100%',
                        height: { xs: 200, sm: 180, md: 200 },
                        objectFit: 'contain',
                        backgroundColor: '#f5f3f0',
                        cursor: 'zoom-in',
                        transition: 'opacity 0.2s ease',
                        '&:hover': { opacity: 0.88 },
                      }}
                    />
                  )}

                  {/* No-image placeholder */}
                  {!imageUrl && (
                    <Box
                      sx={{
                        width: '100%',
                        height: { xs: 120, md: 140 },
                        backgroundColor: '#f0eeeb',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <Typography
                        sx={{
                          fontFamily: '"Audiowide", sans-serif',
                          fontSize: '28px',
                          color: 'rgba(0,0,0,0.1)',
                          userSelect: 'none',
                        }}
                      >
                        LFBBQ
                      </Typography>
                    </Box>
                  )}

                  {/* Content */}
                  <Box sx={{ p: 2, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                        mb: item.description ? 1 : 0,
                      }}
                    >
                      <Typography
                        variant="h6"
                        sx={{
                          fontFamily: 'Roboto, sans-serif',
                          fontWeight: 'bold',
                          color: 'black',
                          fontSize: { xs: '16px', md: '18px' },
                          lineHeight: 1.3,
                          flexShrink: 1,
                          pr: 1,
                        }}
                      >
                        {item.name}
                      </Typography>
                      <Typography
                        sx={{
                          fontFamily: 'Roboto, sans-serif',
                          fontWeight: 'bold',
                          color: 'black',
                          fontSize: { xs: '18px', md: '20px' },
                          flexShrink: 0,
                          whiteSpace: 'nowrap',
                        }}
                      >
                        ${item.price}
                      </Typography>
                    </Box>

                    {item.description && (
                      <Typography
                        variant="body2"
                        sx={{
                          fontFamily: 'Roboto, sans-serif',
                          color: 'rgba(0,0,0,0.55)',
                          fontSize: '14px',
                          lineHeight: 1.5,
                          mt: 'auto',
                        }}
                      >
                        {item.description}
                      </Typography>
                    )}
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Fade>
      )}
    </Container>
  );
};

export default MenuPage;
